import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DedupeRule } from "@sga/shared";
import { apiGet, apiPut, ApiError } from "../lib/api.js";
import { Button } from "../components/Button.js";
import { DedupeRuleCard, type FieldChoice } from "./DedupeRuleCard.js";

/**
 * Duplicate prevention — docs/01-PRODUCT-SPEC.md §7, docs/03-API-SPEC.md §5.
 *
 * The evaluator has worked since Phase 7 and nothing has ever used it, because there was no screen
 * to write a rule on. Every form in the database carries zero rules; the live Edo form has taken
 * 466 submissions from 447 people, and 18 of them applied more than once.
 *
 * Rules are saved as a whole list, in order, because order is what decides the outcome: §7 returns
 * the first rule that matches, so an earlier "warn and allow" would mask a later "refuse".
 */

interface StoredRule extends DedupeRule {
  id: string;
  position: number;
}

const blankRule = (): DedupeRule => ({
  strategy: "composite_key",
  fieldKeys: [],
  scope: "form_cycle",
  action: "block",
  message: null,
  isActive: true,
});

/** Fields worth keying on — a heading or a divider identifies nobody. */
const KEYABLE = new Set([
  "text_single",
  "text_regex",
  "email",
  "phone",
  "number",
  "name",
  "date",
  "dropdown",
  "radio",
]);

export const DedupeRulesPanel = ({
  formId,
  fields,
}: {
  formId: string;
  fields: FieldChoice[];
}): JSX.Element => {
  const queryClient = useQueryClient();
  const [rules, setRules] = useState<DedupeRule[] | null>(null);
  const [conflicts, setConflicts] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const query = useQuery({
    queryKey: ["dedupe-rules", formId],
    queryFn: () => apiGet<{ data: StoredRule[] }>(`/forms/${formId}/dedupe-rules`),
  });

  // Seeded once from the server, then owned locally — this is a whole-list editor, and refetching
  // over a coordinator's half-finished rule would discard it.
  useEffect(() => {
    if (query.data && rules === null) setRules(query.data.data);
  }, [query.data, rules]);

  const save = useMutation({
    mutationFn: () =>
      apiPut<{ data: StoredRule[]; conflicts: Record<string, number> }>(
        `/forms/${formId}/dedupe-rules`,
        { rules: rules ?? [] },
      ),
    onSuccess: async (response) => {
      setRules(response.data);
      setConflicts(response.conflicts);
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ["dedupe-rules", formId] });
    },
    onError: (caught) => {
      setError(caught instanceof ApiError ? caught.message : "These rules could not be saved.");
    },
  });

  const update = (next: DedupeRule[]): void => {
    setRules(next);
    setSaved(false);
    setError(null);
  };

  const keyable = fields.filter((field) => KEYABLE.has(field.type));

  if (query.isLoading || rules === null) {
    return <p className="text-sm text-muted-foreground">Loading duplicate-prevention rules…</p>;
  }

  return (
    <div className="max-w-3xl space-y-4">
      <header>
        <h2 className="font-heading text-lg font-semibold">Duplicate prevention</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Rules are checked in order when someone submits, and the first one that matches decides
          what happens. A rule set to <strong>refuse</strong> is backed by a database constraint, so
          two browser tabs on deadline day cannot both get through; the other actions are advisory.
        </p>
      </header>

      {rules.length === 0 ? (
        <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
          This form has no rules, so nothing prevents one person applying twice.
        </p>
      ) : (
        <ul className="space-y-3">
          {rules.map((rule, index) => (
            <DedupeRuleCard
              key={rule.id ?? `new-${index}`}
              rule={rule}
              index={index}
              total={rules.length}
              fields={keyable}
              conflicts={rule.id ? conflicts[rule.id] : undefined}
              onChange={(next) => update(rules.map((entry, i) => (i === index ? next : entry)))}
              onMove={(direction) => {
                const target = index + direction;
                if (target < 0 || target >= rules.length) return;
                const next = [...rules];
                const [moved] = next.splice(index, 1);
                if (moved) next.splice(target, 0, moved);
                update(next);
              }}
              onRemove={() => update(rules.filter((_, i) => i !== index))}
            />
          ))}
        </ul>
      )}

      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={() => update([...rules, blankRule()])}>
          Add a rule
        </Button>
        <Button
          variant="primary"
          onClick={() => save.mutate()}
          disabled={save.isPending || keyable.length === 0}
        >
          {save.isPending ? "Saving…" : "Save rules"}
        </Button>
        {saved && !save.isPending && (
          <span role="status" className="text-xs text-primary">
            Saved. In force for every submission from now on.
          </span>
        )}
      </div>

      {error && (
        <p role="alert" className="rounded-md bg-brand-red/10 p-2 text-sm text-brand-red">
          {error}
        </p>
      )}
    </div>
  );
};
